import { CasoFolder } from '@/types/casos';
import { Card } from '@/components/ui/card';
import {
  Folder,
  FolderOpen,
  FolderArchive,
  FolderCheck,
  Users,
  Building,
  Scale,
  FileText,
  Briefcase,
  Home,
  Star,
  Archive,
  MoreVertical,
  Edit,
  Trash2
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { useDeleteCasoFolder } from '@/hooks/useCasoFolders';
import { useDroppable } from '@dnd-kit/core';

const ICON_MAP: Record<string, any> = {
  'folder': Folder,
  'folder-open': FolderOpen,
  'folder-archive': FolderArchive,
  'folder-check': FolderCheck,
  'users': Users,
  'building': Building,
  'scale': Scale,
  'briefcase': Briefcase,
  'file-text': FileText,
  'home': Home,
  'star': Star,
  'archive': Archive,
};

const COLOR_MAP: Record<string, string> = {
  blue: 'text-blue-500',
  green: 'text-green-500',
  purple: 'text-purple-500',
  orange: 'text-orange-500',
  red: 'text-red-500',
  pink: 'text-pink-500',
  yellow: 'text-yellow-500',
  teal: 'text-teal-500',
  indigo: 'text-indigo-500',
  gray: 'text-gray-500',
};

const getFolderIcon = (iconId: string = 'folder') => {
  return ICON_MAP[iconId] || FolderOpen;
};

interface FolderCardProps {
  folder: CasoFolder;
  onEdit: (folder: CasoFolder) => void;
}

export function FolderCard({ folder, onEdit }: FolderCardProps) {
  const navigate = useNavigate();
  const { mutate: deleteFolder } = useDeleteCasoFolder();

  const { setNodeRef: setDroppableRef, isOver } = useDroppable({
    id: `folder-${folder.id}`,
    data: { type: 'folder', folder },
  });

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirm(`Excluir pasta "${folder.name}"?`)) {
      deleteFolder(folder.id);
    }
  };

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation();
    onEdit(folder);
  };

  const handleClick = () => {
    navigate(`/casos?folder=${folder.id}`);
  };

  const FolderIcon = getFolderIcon(folder.icon);
  const folderColor = folder.color || 'blue';

  return (
    <Card
      ref={setDroppableRef}
      onClick={handleClick}
      className={`
        p-3 cursor-pointer transition-all hover:shadow-lg
        ${isOver ? 'ring-2 ring-primary ring-offset-2 bg-accent' : ''}
      `}
    >
      <div className="flex items-center gap-3">
        <FolderIcon className={`h-5 w-5 flex-shrink-0 ${COLOR_MAP[folderColor] || 'text-primary'}`} />
        
        <h3 className="font-medium text-sm truncate flex-1 min-w-0">
          {folder.name}
        </h3>

        <DropdownMenu>
          <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
            <Button variant="ghost" size="icon" className="h-8 w-8 flex-shrink-0">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={handleEdit}>
              <Edit className="mr-2 h-4 w-4" />
              Editar
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleDelete} className="text-destructive">
              <Trash2 className="mr-2 h-4 w-4" />
              Excluir
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </Card>
  );
}
